import React, { useEffect, useState } from "react";
import { Control, Controller, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { Col, Row } from "antd";
import { Checkbox, FormHelperText } from "@mui/material";
import { useWatch } from "react-hook-form";

import TextInput from "shared/components/input/TextInput";
import CustomButton from "shared/components/button/CustomButton";
import CustomSelect from "shared/components/select/CustomSelect";
import { SelectOptionType } from "types/select/SelectOptionType";

import { ConfigurarAcessoDiretoForm } from "../ConfigurarAcessoDireto";
import { useGetConfiguracaoSmtpPadrao } from "hooks/groupPay/useGetConfiguracaoSmtpPadrao";

interface PortEncryption {
  port: string;
  encryption: string;
}

interface SmtpConfigPayload {
  server: string;
  protocol?: string;
  portEncryption: PortEncryption[];
}

interface FormularioAcessoDiretoProps {
  control: Control<ConfigurarAcessoDiretoForm>;
  register: UseFormRegister<ConfigurarAcessoDiretoForm>;
  setValue: UseFormSetValue<ConfigurarAcessoDiretoForm>;
  opcoesProtocolo: SelectOptionType<string>[];
  opcoesCriptografia: SelectOptionType<string>[];
  onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  onShowTermos: () => void;
  classes: Record<string, string>;
  isTesting: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FormularioAcessoDireto: React.FC<FormularioAcessoDiretoProps> = ({
  control,
  register,
  setValue,
  opcoesProtocolo,
  opcoesCriptografia,
  onSubmit,
  onShowTermos,
  classes,
  isTesting,
}) => {
  const email = useWatch({ control, name: "email" });

  const [debouncedEmailValue, setDebouncedEmailValue] = useState(email || "");
  const DEBOUNCE_DELAY = 1000;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmailValue(email || "");
    }, DEBOUNCE_DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [email]);

  const isEmailValidForQuery = debouncedEmailValue && EMAIL_REGEX.test(debouncedEmailValue.trim());
  const queryEmailArgument = isEmailValidForQuery ? debouncedEmailValue : "";

  const configuracaoSmtpQuery = useGetConfiguracaoSmtpPadrao(queryEmailArgument);

  useEffect(() => {
    const data = configuracaoSmtpQuery.data;
    const payload = data?.payload as SmtpConfigPayload | undefined;

    if (!isEmailValidForQuery) {
      return;
    }

    const revalidateOptions = { shouldValidate: true };

    if (payload) {
      setValue("servidor", payload.server, revalidateOptions);

      const firstPortEncryption = payload.portEncryption?.[0];

      if (firstPortEncryption) {
        setValue("porta", Number(firstPortEncryption.port), revalidateOptions);

        const encryptionValue = firstPortEncryption.encryption.toLowerCase();

        const criptografiaOption = opcoesCriptografia.find(
          (opt) => opt.label.toLowerCase() === encryptionValue || opt.value.toLowerCase() === encryptionValue
        );

        if (criptografiaOption) {
          setValue("tipoCriptografia", criptografiaOption, revalidateOptions);
        } else {
          if (opcoesCriptografia.length > 0) {
            setValue("tipoCriptografia", opcoesCriptografia[0], revalidateOptions);
          } else {
            setValue("tipoCriptografia", { label: "", value: "" }, revalidateOptions);
          }
        }
      } else {
        setValue("porta", 0, revalidateOptions);
        setValue("tipoCriptografia", { label: "", value: "" }, revalidateOptions);
      }

      const returnedProtocol = payload.protocol?.toLowerCase();
      if (returnedProtocol) {
        const protocoloOption = opcoesProtocolo.find(
          (opt) => opt.label.toLowerCase() === returnedProtocol || opt.value.toLowerCase() === returnedProtocol
        );
        if (protocoloOption) {
          setValue("tipoProtocolo", protocoloOption, revalidateOptions);
        } else {
          setValue("tipoProtocolo", { label: "", value: "" }, revalidateOptions);
        }
      } else {
        setValue("tipoProtocolo", { label: "", value: "" }, revalidateOptions);
      }
    }
  }, [
    configuracaoSmtpQuery.data,
    configuracaoSmtpQuery.isLoading,
    setValue,
    opcoesProtocolo,
    opcoesCriptografia,
    isEmailValidForQuery,
  ]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(e);
      }}
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12}>
          <TextInput
            id="email"
            name="email"
            label="E-mail"
            placeholder="Informe o e-mail"
            control={control}
            register={register}
            setValue={setValue}
            usarValidacaoParaFormulario
          />
        </Col>
        <Col xs={24} sm={12}>
          <TextInput
            id="senha"
            name="senha"
            label="Senha"
            placeholder="Informe a senha"
            isPassword={true}
            control={control}
            register={register}
            setValue={setValue}
            usarValidacaoParaFormulario
          />
        </Col>
        <Col xs={24} sm={12}>
          <TextInput
            id="servidor"
            name="servidor"
            label="Servidor"
            placeholder="Informe o servidor"
            control={control}
            register={register}
            setValue={setValue}
            usarValidacaoParaFormulario
          />
        </Col>
        <Col xs={24} sm={12}>
          <TextInput
            id="porta"
            name="porta"
            label="Porta"
            placeholder="Informe a porta"
            control={control}
            register={register}
            setValue={setValue}
            usarValidacaoParaFormulario
          />
        </Col>
        <Col xs={24} sm={12}>
          <CustomSelect
            id="tipoProtocolo"
            name="tipoProtocolo"
            label="Tipo de protocolo"
            options={opcoesProtocolo}
            control={control}
            register={register}
            setValue={setValue}
            usarValidacaoParaFormulario
            isClearable
          />
        </Col>
        <Col xs={24} sm={12}>
          <CustomSelect
            id="tipoCriptografia"
            name="tipoCriptografia"
            label="Tipo de criptografia"
            options={opcoesCriptografia}
            control={control}
            register={register}
            setValue={setValue}
            usarValidacaoParaFormulario
            isClearable
          />
        </Col>
        <Col xs={24}>
          <Controller
            name="aceiteTermos"
            control={control}
            render={({ field, fieldState }) => (
              <div className={classes.checkboxContainer}>
                <div className={classes.checkboxWrapper}>
                  <Checkbox {...field} checked={field.value} id="aceiteTermosCheckbox" sx={{ paddingLeft: 0 }} />
                  <label htmlFor="aceiteTermosCheckbox" className={classes.labelTermos}>
                    Li e concordo com os{" "}
                    <span
                      className={classes.link}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onShowTermos();
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      termos de uso
                    </span>
                    .
                  </label>
                </div>
                {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
              </div>
            )}
          />
        </Col>
      </Row>
      <div className={classes.footer}>
        <CustomButton
          id="testar-acesso-btn"
          label={"Realizar acesso"}
          variant="primary"
          type="submit"
          disabled={isTesting}
        />
      </div>
    </form>
  );
};

export default FormularioAcessoDireto;
